// C:\Users\aleja\Code\inventory_analytics\src\front\src\store\flux\machineCatalogActions.js
import getApiBaseUrl from "../../utils/api";

const API_URL = getApiBaseUrl();

const normalizeQueryValue = (value) => {
  if (typeof value === "string") return value.trim();
  return value;
};

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, rawValue]) => {
    if (rawValue === undefined || rawValue === null) return;

    const values = Array.isArray(rawValue)
      ? rawValue
      : [rawValue];

    values.forEach((value) => {
      const normalized = normalizeQueryValue(value);

      if (normalized === "") return;

      searchParams.append(key, String(normalized));
    });
  });

  const query = searchParams.toString();
  return query ? `?${query}` : "";
};

const parseResponsePayload = async (response) => {
  const text = await response.text();

  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (_error) {
    return { message: text };
  }
};

const extractErrorMessage = (payload, fallback) =>
  payload?.error ||
  payload?.message ||
  payload?.msg ||
  fallback;

const machineCatalogActions = ({
  getStore,
  getActions,
}) => {
  const request = async ({
    path,
    method = "GET",
    body,
    signal,
  }) => {
    const { token } = getStore();
    const headers = {
      Accept: "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }

    let response;

    try {
      response = await fetch(`${API_URL}${path}`, {
        method,
        headers,
        body:
          body === undefined
            ? undefined
            : JSON.stringify(body),
        signal,
      });
    } catch (error) {
      if (error?.name === "AbortError") {
        return {
          success: false,
          data: null,
          error: "Request aborted",
          code: "REQUEST_ABORTED",
          field: null,
          details: null,
          status: 0,
          aborted: true,
        };
      }

      return {
        success: false,
        data: null,
        error:
          error?.message ||
          "No se pudo conectar con el servidor",
        code: "NETWORK_ERROR",
        field: null,
        details: null,
        status: 0,
      };
    }

    const payload = await parseResponsePayload(response);

    if (response.status === 401) {
      const handleApiError = getActions()?.handleApiError;

      if (typeof handleApiError === "function") {
        await Promise.resolve(handleApiError(response));
      }
    }

    if (!response.ok || payload?.success === false) {
      return {
        success: false,
        data: payload?.data || null,
        error: extractErrorMessage(
          payload,
          `Error HTTP ${response.status}`,
        ),
        code: payload?.code || null,
        field: payload?.field || null,
        details: payload?.details || null,
        status: response.status,
      };
    }

    return {
      success: true,
      data:
        payload &&
        Object.prototype.hasOwnProperty.call(
          payload,
          "data",
        )
          ? payload.data
          : payload,
      error: null,
      code: null,
      field: null,
      details: null,
      status: response.status,
    };
  };

  return {
    getFactoryUnits: async (
      params = {},
      options = {},
    ) =>
      request({
        path:
          "/api/factory-units" +
          buildQueryString(params),
        signal: options.signal,
      }),

    getFactoryUnit: async (
      unitId,
      options = {},
    ) =>
      request({
        path:
          "/api/factory-units/" +
          encodeURIComponent(unitId),
        signal: options.signal,
      }),

    createFactoryUnit: async (
      payload,
      options = {},
    ) =>
      request({
        path: "/api/factory-units",
        method: "POST",
        body: payload,
        signal: options.signal,
      }),

    updateFactoryUnit: async (
      unitId,
      payload,
      options = {},
    ) =>
      request({
        path:
          "/api/factory-units/" +
          encodeURIComponent(unitId),
        method: "PATCH",
        body: payload,
        signal: options.signal,
      }),

    getMachines: async (
      params = {},
      options = {},
    ) =>
      request({
        path:
          "/api/machines" +
          buildQueryString(params),
        signal: options.signal,
      }),

    getMachine: async (
      machineId,
      options = {},
    ) =>
      request({
        path:
          "/api/machines/" +
          encodeURIComponent(machineId),
        signal: options.signal,
      }),

    createMachine: async (
      payload,
      options = {},
    ) =>
      request({
        path: "/api/machines",
        method: "POST",
        body: payload,
        signal: options.signal,
      }),

    updateMachine: async (
      machineId,
      payload,
      options = {},
    ) =>
      request({
        path:
          "/api/machines/" +
          encodeURIComponent(machineId),
        method: "PATCH",
        body: payload,
        signal: options.signal,
      }),

    getMachineRelatedMaterials: async (
      machineId,
      params = {},
      options = {},
    ) =>
      request({
        path:
          "/api/machines/" +
          encodeURIComponent(machineId) +
          "/related-materials" +
          buildQueryString(params),
        signal: options.signal,
      }),

    getMachineSpareParts: async (
      machineId,
      params = {},
      options = {},
    ) =>
      request({
        path:
          "/api/machines/" +
          encodeURIComponent(machineId) +
          "/spare-parts" +
          buildQueryString(params),
        signal: options.signal,
      }),

    getMachineSparePart: async (
      relationId,
      options = {},
    ) =>
      request({
        path:
          "/api/machine-spare-parts/" +
          encodeURIComponent(relationId),
        signal: options.signal,
      }),

    createMachineSparePart: async (
      machineId,
      payload,
      options = {},
    ) =>
      request({
        path:
          "/api/machines/" +
          encodeURIComponent(machineId) +
          "/spare-parts",
        method: "POST",
        body: payload,
        signal: options.signal,
      }),

    updateMachineSparePart: async (
      relationId,
      payload,
      options = {},
    ) =>
      request({
        path:
          "/api/machine-spare-parts/" +
          encodeURIComponent(relationId),
        method: "PATCH",
        body: payload,
        signal: options.signal,
      }),

    getMaterialMachines: async (
      materialRecordId,
      params = {},
      options = {},
    ) =>
      request({
        path:
          "/api/materials/" +
          encodeURIComponent(materialRecordId) +
          "/machines" +
          buildQueryString(params),
        signal: options.signal,
      }),

    searchMachineSpareMaterials: async (
      params = {},
      options = {},
    ) =>
      request({
        path:
          "/api/catalog/orma-catalog/search" +
          buildQueryString(params),
        signal: options.signal,
      }),
  };
};

export default machineCatalogActions;
